import { z } from "zod";

// Card types and scoring
export const CardTypes = {
  A: "♠️", // Spades
  B: "♥️", // Hearts
  C: "♣️", // Clubs
  D: "♦️", // Diamonds
} as const;

export type CardType = keyof typeof CardTypes;

export const NumberMap: Record<string, string> = {
  "11": "J",
  "12": "Q",
  "13": "K",
};

// Generate six to king deck
export function getSixToKingDeck(): string[] {
  const suits: CardType[] = ["A", "B", "C", "D"];
  const deck: string[] = [];

  for (const suit of suits) {
    for (let value = 6; value <= 13; value++) {
      deck.push(`${suit}${value}`);
    }
  }

  return deck;
}

// Compare two cards following game rules
export function biggerCard(
  cardOneIndex: string,
  cardTwoIndex: string,
): string | "00" | "99" {
  // Input validation
  if (
    !cardOneIndex ||
    !cardTwoIndex ||
    typeof cardOneIndex !== "string" ||
    typeof cardTwoIndex !== "string" ||
    cardOneIndex.length < 2 ||
    cardTwoIndex.length < 2
  ) {
    return "00";
  }

  const suit1 = cardOneIndex[0];
  const suit2 = cardTwoIndex[0];

  // Validate suits
  if (
    !["A", "B", "C", "D"].includes(suit1) ||
    !["A", "B", "C", "D"].includes(suit2)
  ) {
    return "00";
  }

  if (suit1 === suit2) {
    const value1 = Number(cardOneIndex.substring(1));
    const value2 = Number(cardTwoIndex.substring(1));

    // Validate card values (6-13 range)
    if (
      isNaN(value1) ||
      isNaN(value2) ||
      value1 < 6 ||
      value1 > 13 ||
      value2 < 6 ||
      value2 > 13
    ) {
      return "00";
    }

    if (value1 > value2) return cardOneIndex;
    else if (value1 === value2) return "99";
    else return cardTwoIndex;
  } else {
    return "00";
  }
}

// Check for invalid card play (Gbaa rule)
export function isGbaa(
  playedCard: string,
  lastPlayedCard: string,
  playerCards: string[],
  isLeading: boolean,
): boolean {
  // If player has isLeading, they can play any card
  if (isLeading) {
    return false;
  }

  // Check if player has any cards of the same suit as last played
  const hasSameSuitCard = playerCards.some(
    (card) => card[0] === lastPlayedCard[0],
  );

  // If player has same suit but played different suit
  if (hasSameSuitCard && playedCard[0] !== lastPlayedCard[0]) {
    return true;
  }

  return false;
}



// Shuffle deck using Fisher-Yates algorithm
export function shuffle<T>(array: T[]): T[] {
  const shuffled = [...array];
  let elementsRemaining = shuffled.length;

  while (elementsRemaining > 1) {
    const randomIndex = Math.floor(Math.random() * elementsRemaining--);
    if (randomIndex !== elementsRemaining) {
      const temp = shuffled[elementsRemaining];
      shuffled[elementsRemaining] = shuffled[randomIndex];
      shuffled[randomIndex] = temp;
    }
  }

  return shuffled;
}

// Can capture leading card if same suit and higher value
export function canCaptureLeading(
  playedCard: string,
  lastPlayedCard: string,
): boolean {
  // Can only capture if same suit and higher value
  if (playedCard[0] === lastPlayedCard[0]) {
    const playedValue = Number(playedCard.substring(1));
    const lastValue = Number(lastPlayedCard.substring(1));
    return playedValue > lastValue;
  }
  return false;
}

// Check if all cards in a deck are under 10
export function isUnder10(cardDeck: string[]): boolean {
  if (cardDeck.length < 5) {
    console.log("Player already played a card");
    return false;
  }

  return cardDeck.every((cardIndex) => Number(cardIndex.substring(1)) < 10);
}

// Schema for game state validation
export const gameStateSchema = z.object({
  player1Cards: z.array(z.string()),
  player2Cards: z.array(z.string()),
  player1PlayedCards: z.array(z.string()),
  player2PlayedCards: z.array(z.string()),
  currentTurn: z.number(),
  scores: z.record(z.number(), z.number()),
  round: z.number(),
  isRoundEnded: z.boolean(),
  lastPlayed: z
    .object({
      player: z.number(),
      card: z.string(),
    })
    .optional(),
});

export type GameState = z.infer<typeof gameStateSchema>;
